import { useContext } from "react";
import { AppContext } from "../App";
import { MdMenu } from "react-icons/md";
import { HiOutlineArrowRight } from "react-icons/hi";
import logo from "../assets/logo.png";

export default function TopBar() {
  const appContext = useContext(AppContext);

  function goTo(item) {
    appContext.navTo({ item });
  }

  const navItems = [
    { label: "Home", key: "home" },
    { label: "Services", key: "services" },
    { label: "About Us", key: "about_us" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@300;400;500;600;700;800&display=swap');

        :root {
          --sunflower-amber: #C9A227;
          --text-navy: #0A0F1C;
          --text-slate: #64748B;
          --bg-cream: #FAF7F2;
          --border-subtle: rgba(15, 23, 42, 0.08);
          --font-corporate: 'Libre Franklin', sans-serif;
        }

        /* Override default container padding if needed */
        .topbar-modern-body {
          padding: 0 48px;
          height: 80px;
          display: flex;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        /* --- BRAND / LOGO --- */
        .hospital-topbar__brand {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 12px;
          opacity: 1;
        }
        
        .hospital-topbar__brand img {
          height: 42px;
          width: auto;
          object-fit: contain;
        }

        .brand-text-group {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          line-height: 1.1;
        }
        .brand-text-group strong {
          font-size: 0.95rem;
          font-weight: 800;
          color: var(--text-navy);
          letter-spacing: -0.01em;
        }
        .brand-text-group span {
          font-size: 0.65rem;
          font-weight: 600;
          color: var(--sunflower-amber);
          text-transform: uppercase;
          letter-spacing: 0.15em;
        }

        /* --- NAVIGATION --- */
        .topbar-modern-nav-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 60px;
        }

        .topbar-modern-link {
          background: none;
          border: none;
          padding: 10px 18px;
          font-family: var(--font-corporate);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-slate);
          cursor: pointer;
          position: relative;
          transition: color 0.3s ease;
        }

        .topbar-modern-link::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 50%;
          width: 0;
          height: 2px;
          background: var(--sunflower-amber);
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          transform: translateX(-50%);
        }

        .topbar-modern-link:hover {
          color: var(--text-navy);
        }

        .topbar-modern-link:hover::after {
          width: 20px;
        }

        .topbar-modern-link.active {
          color: var(--text-navy);
        }

        .topbar-modern-link.active::after {
          width: 20px;
          background: var(--sunflower-amber);
        }

        /* --- CTA BUTTON --- */
        .hospital-topbar__cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 24px;
          background: var(--text-navy);
          color: #fff;
          text-decoration: none;
          border-radius: 100px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid var(--text-navy);
          cursor: pointer;
        }

        .hospital-topbar__cta svg {
          transition: transform 0.3s ease;
        }

        .hospital-topbar__cta:hover {
          background: var(--sunflower-amber);
          border-color: var(--sunflower-amber);
          color: var(--text-navy);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px -5px rgba(201, 162, 39, 0.4);
        }

        .hospital-topbar__cta:hover svg {
          transform: translateX(3px);
        }

        /* --- MOBILE MENU BUTTON --- */
        .topbar-modern-menu-btn {
          background: none;
          border: 1px solid var(--border-subtle);
          width: 44px;
          height: 44px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-navy);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .topbar-modern-menu-btn:hover {
          background: var(--text-navy);
          color: #fff;
          border-color: var(--text-navy);
        }

        /* --- RESPONSIVE --- */
        @media (max-width: 992px) {
          .topbar-modern-body { padding: 0 24px; height: 70px; }
          .topbar-modern-nav-wrap { display: none; } /* Hide desktop nav on mobile */
          .hospital-topbar__cta { display: none; } /* Hide CTA on mobile to save space */
          .brand-text-group { display: none; } /* Hide text next to logo on mobile */
        }

        @media (max-width: 576px) {
          .topbar-modern-body { padding: 0 16px; }
          .hospital-topbar__brand img { height: 36px; }
        }
      `}</style>

      <div className="TopBar topbar-modern">
        <div className="topbar-modern-body">
          <div className="d-flex align-items-center justify-content-between w-100">
            
            {/* 1. Brand / Logo */}
            <button type="button" className="hospital-topbar__brand" onClick={() => goTo("home")}>
              <img src={logo} alt="Logo" />
            </button>

            {/* 2. Desktop Navigation */}
            <div className="topbar-modern-nav-wrap mDesktopOnly">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  className={`topbar-modern-link ${appContext.navItem === item.key ? "active" : ""}`}
                  onClick={() => goTo(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* 3. Actions (CTA + Mobile Menu) */}
            <div className="d-flex align-items-center gap-3">
              {/* Desktop CTA */}
              <button
                className="hospital-topbar__cta d-none d-md-flex"
                onClick={() => goTo("contact_us")}
              >
                Contact Us <HiOutlineArrowRight size={16} />
              </button>

              {/* Mobile Menu Toggle */}
              <button
                className="topbar-modern-menu-btn d-md-none"
                onClick={appContext.openSideBar}
                aria-label="Open menu"
              >
                <MdMenu size={24} />
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}